import { observer } from 'mobx-react';
import React from 'react';
import Skeleton from 'react-loading-skeleton';
import { Icons } from 'src/assets/fonts/Icons/icons';
import { SCREENS } from 'src/constants/screens';
import { COLORS } from 'src/constants/styles';
import { localeStore } from 'src/mobx/localesStore';
import { history } from 'src/services/history';
import { ICurrency } from 'src/types/currency';
import { LoadingStatus } from 'src/types/main';
import { confirmDelete, confirmMessage } from 'src/utils/modalConfirmations';
import { TOOLTIP_DURATION, TOOLTIP_POSITION } from 'src/view/components/Controls/CustomTooltip/CustomTooltip';
import Icon from 'src/view/components/Controls/Icon/Icon';
import Text, { TEXT } from 'src/view/components/Text/Text';
import currenciesStore from 'src/view/screens/SettingsScreen/store/currenciesStore';
import { BUTTON_TITLE } from '~/constants/main';
import Button from '~/view/components/Controls/ButtonComponent/Button';
import Label from '~/view/components/Controls/Label';
import TooltipIcon from '~/view/components/TooltipIcon/TooltipIcon';

interface IProps {
  currency?: ICurrency;
  index?: number;
}

const CurrenciesListRaw: React.FC<IProps> = observer((props: IProps): JSX.Element => {
  const disabled: boolean = [LoadingStatus.Init, LoadingStatus.Refreshing, LoadingStatus.Loading].includes(currenciesStore.currenciesLoadingStatus);

  const editCurrency = (event: React.MouseEvent<HTMLDivElement>) => {
    if (
      !props.currency ||
      (event.target as HTMLDivElement).classList.contains('icon-warning') ||
      (event.target as HTMLDivElement).classList.contains('tooltip__overlay')
    ) {
      return;
    }
    history.push(SCREENS.SETTINGS_CURRENCY_EDIT(props.currency.id));
  };

  const deleteCurrency = () => {
    if (!props.currency) {
      return;
    }
    confirmDelete({
      title: localeStore.t('delete_currency_header'),
      message: localeStore.t('txt_currency_will_be_deleted'),
      onConfirm: () => props.currency && currenciesStore.deleteCurrency(props.currency.id),
      redConfirmBtn: true,
    });
  };

  const setAsDefault = () => {
    if (!props.currency) {
      return;
    }
    confirmMessage({
      title: localeStore.t('set_default_currency_header'),
      message: localeStore.t('txt_set_currency_as_default', { currency_name: props.currency.currency_name }),
      onConfirm: () => props.currency && currenciesStore.setAsDefault(props.currency.id),
      confirmBtnTxt: localeStore.t(BUTTON_TITLE.SET_DEFAULT),
      cancelBtnTxt: localeStore.t(BUTTON_TITLE.CANCEL),
    });
  };

  const { currency } = props;
  return (
    <li className={`items-list-row ${currency?.default_currency ? 'items-list-row__my_account' : ''}`}>
      {/* currency name */}
      <div onClick={editCurrency} className="items-list-row__border d-flex flex-row align-items-center cursor-pointer w-20">
        {currency ? <Text type={TEXT.LIST_ROW} text={currency.currency_name} noTranslate defaultValue="" /> : <Skeleton width={70} height={19.2} />}
      </div>
      {/* currency code */}
      <div onClick={editCurrency} className="items-list-row__border d-flex flex-row align-items-center cursor-pointer w-17">
        {currency ? <Text type={TEXT.LIST_ROW} text={currency.currency_symbol} defaultValue="" /> : <Skeleton width={50} height={19.2} />}
      </div>
      {/* currency code ar */}
      <div onClick={editCurrency} className="items-list-row__border d-flex flex-row align-items-center cursor-pointer w-15">
        {currency ? <Text type={TEXT.LIST_ROW} text={currency.currency_code} noTranslate defaultValue="" /> : <Skeleton width={70} height={19.2} />}
      </div>
      {/* currency symbol */}
      <div onClick={editCurrency} className="items-list-row__border d-flex flex-row align-items-center cursor-pointer w-15">
        {currency ? <Text type={TEXT.LIST_ROW} text={currency.currency_code_ar} defaultValue="" /> : <Skeleton width={70} height={19.2} />}
      </div>
      {/* multiplier */}
      <div onClick={editCurrency} className="items-list-row__border d-flex flex-row align-items-center cursor-pointer w-14">
        {currency ? <Text type={TEXT.LIST_ROW} text={currency.divided} defaultValue="" /> : <Skeleton width={50} height={19.2} />}
      </div>
      {/* status */}
      {currency?.default_currency ? (
        <div className="items-list-row__border d-flex flex-row align-items-center cursor-pointer w-19 justify-content-between">
          <Label text={localeStore.t('txt_default_currency')} color={COLORS.MAINBLUE} />
          <TooltipIcon
            message="tooltip_default_currency_delete"
            duration={TOOLTIP_DURATION.EXTRA_LARGE}
            position={TOOLTIP_POSITION.PORTAL}
            maxWidth={350}
            iconSize={24}
            iconClassName="mr-2"
          />
        </div>
      ) : (
        <div className="items-list-row__border d-flex flex-row align-items-center cursor-pointer w-19 justify-content-between">
          {/* status */}
          {currency ? (
            <>
              <Button title={localeStore.t('txt_set_as_default')} onClick={setAsDefault} size="small" appearance="tertiary" />

              <Icon
                name={Icons.Trash}
                onPress={deleteCurrency}
                size={24}
                disabled={disabled}
                className="delete-restore-block__btn-delete mr-2"
                disabledClassName="delete-restore-block__btn-delete_disabled"
              />
            </>
          ) : (
            <Skeleton width={180} height={19.2} />
          )}
        </div>
      )}
    </li>
  );
});

export default CurrenciesListRaw;
